import DesignButton from 'components/button/DesignButton';
import DesignCard from 'components/card/DesignCard';
import DesignInput from 'components/input/DesignInput';
import { PlusIcon } from 'components/svgs';
import useAppDispatch from 'core/hooks/useAppDispatch';
import useAppSelector from 'core/hooks/useAppSelector';
import {
  addBreak,
  patchSettingsDraft,
  removeBreak,
  selectSettingsDraft,
  updateBreak,
} from 'core/store/CapacityPlanningSlice';
import { ReactNode } from 'react';
import { breakDurationMinutesRange, labelMaxLength } from './RunSettings';

export type RunSettingsFormLayout = 'stack' | 'grid';

export interface RunSettingsFormProps {
  disabled: boolean;
  /** 'stack' for the New run column, 'grid' for the two-column Settings tab. */
  layout?: RunSettingsFormLayout;
}

/** Shown in place of the Planning Depot fields, which no Run edits any more. */
export const depotFromFileNote =
  'Depot coordinates come from the file: DepotLatitude and DepotLongitude are required columns.';

/** Shown in place of the Driver fields, which the file now decides. */
export const driverFromFileNote =
  'Service times come from the file: ParkingTime per stop, DeliveryTime per recipient and ' +
  'ApproximateLocationAdditionalTime for an approximated location. Each route is bounded by the ' +
  'shift window below.';

const layoutClasses: Record<RunSettingsFormLayout, string> = {
  stack: 'flex flex-col gap-6',
  grid: 'grid grid-cols-2 gap-6',
};

const labelClasses = 'text-sm text-light-black dark:text-sublte-white';
const helpClasses = 'mt-2 text-xs text-light-black dark:text-sublte-white';

/** `DesignInput`'s own classes; the Label needs `maxLength`, which it lacks. */
const textInputClasses =
  'mt-2 w-full rounded-sm p-2 leading-6 outline outline-1 hover:outline-design-red dark:bg-design-black dark:[color-scheme:dark]';

const Field = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col">
    <label className={labelClasses} htmlFor={id}>
      {label}
    </label>
    {children}
  </div>
);

/**
 * The Run Settings cards, bound to the Redux draft. Used both by the New run
 * form and by an existing run's Settings tab.
 */
const RunSettingsForm = ({ disabled, layout }: RunSettingsFormProps) => {
  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectSettingsDraft);

  return (
    <div className={layoutClasses[layout ?? 'stack']}>
      <DesignCard title="Label">
        <Field id="runLabel" label="Label">
          <input
            id="runLabel"
            name="runLabel"
            type="text"
            maxLength={labelMaxLength}
            readOnly={disabled}
            className={textInputClasses}
            value={draft.label}
            onChange={(event) =>
              dispatch(patchSettingsDraft({ label: event.target.value }))
            }
          />
        </Field>
        <p className={helpClasses}>
          Names the run for humans; defaults to the file name.
        </p>
      </DesignCard>

      <DesignCard title="Planning depots">
        <p className="text-sm">{depotFromFileNote}</p>
      </DesignCard>

      <DesignCard title="Driver">
        <p className="text-sm">{driverFromFileNote}</p>
      </DesignCard>

      <DesignCard title="Shift window">
        <div className="grid grid-cols-2 gap-4">
          <Field id="shiftStart" label="Start">
            <DesignInput
              id="shiftStart"
              name="shiftStart"
              type="time"
              step={60}
              readonly={disabled}
              value={draft.shiftStart}
              onChanceHandler={(event) =>
                dispatch(patchSettingsDraft({ shiftStart: event.target.value }))
              }
            />
          </Field>
          <Field id="shiftEnd" label="End">
            <DesignInput
              id="shiftEnd"
              name="shiftEnd"
              type="time"
              step={60}
              readonly={disabled}
              value={draft.shiftEnd}
              onChanceHandler={(event) =>
                dispatch(patchSettingsDraft({ shiftEnd: event.target.value }))
              }
            />
          </Field>
        </div>
      </DesignCard>

      <DesignCard title="Breaks">
        <div className="flex flex-col gap-4">
          {draft.breaks.length === 0 && <p className="text-sm">No breaks.</p>}
          {draft.breaks.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div
              key={`break-${index}`}
              className="grid grid-cols-4 items-end gap-4"
            >
              <Field id={`breakWindowStart-${index}`} label="Window start">
                <DesignInput
                  id={`breakWindowStart-${index}`}
                  name={`breakWindowStart-${index}`}
                  type="time"
                  step={60}
                  readonly={disabled}
                  value={item.windowStart}
                  onChanceHandler={(event) =>
                    dispatch(
                      updateBreak({
                        index,
                        patch: { windowStart: event.target.value },
                      }),
                    )
                  }
                />
              </Field>
              <Field id={`breakWindowEnd-${index}`} label="Window end">
                <DesignInput
                  id={`breakWindowEnd-${index}`}
                  name={`breakWindowEnd-${index}`}
                  type="time"
                  step={60}
                  readonly={disabled}
                  value={item.windowEnd}
                  onChanceHandler={(event) =>
                    dispatch(
                      updateBreak({
                        index,
                        patch: { windowEnd: event.target.value },
                      }),
                    )
                  }
                />
              </Field>
              <Field id={`breakDuration-${index}`} label="Duration (minutes)">
                <DesignInput
                  id={`breakDuration-${index}`}
                  name={`breakDuration-${index}`}
                  type="number"
                  min={breakDurationMinutesRange.min}
                  max={breakDurationMinutesRange.max}
                  step={1}
                  readonly={disabled}
                  value={item.durationMinutes}
                  onChanceHandler={(event) =>
                    dispatch(
                      updateBreak({
                        index,
                        patch: { durationMinutes: event.target.value },
                      }),
                    )
                  }
                />
              </Field>
              <DesignButton
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClickHandler={() => dispatch(removeBreak(index))}
              >
                Remove
              </DesignButton>
            </div>
          ))}
          <div>
            <DesignButton
              variant="ghost"
              disabled={disabled}
              onClickHandler={() => dispatch(addBreak())}
            >
              <PlusIcon />
              Add break
            </DesignButton>
          </div>
        </div>
      </DesignCard>
    </div>
  );
};

RunSettingsForm.defaultProps = {
  layout: 'stack',
};

export default RunSettingsForm;
